<template>
  <div ref="root" class="confetti-select">
    <!-- eslint-disable-next-line vuejs-accessibility/label-has-for -->
    <label class="confetti-select-container">
      <input
        class="confetti-select-input"
        :disabled="disabled || loading"
        :value="displayedValue"
        :data-input="displayedValue"
        readonly
        @click="toggleList"
      />
      <span class="confetti-select-icon" :class="iconClass">
        <ConfettiIcon icon="angle-down-reg" />
      </span>
      <ul
        class="confetti-select-option-list"
        :class="{ 'confetti-select-option-list--open': showList }"
      >
        <template v-if="isMultipleSelect">
          <li
            v-for="option in options"
            :key="option.value"
            class="confetti-select-option-item multiple-select-option-item"
          >
            <ConfettiCheckbox
              :label="option.text"
              :checked="
                multipleSelected.some(
                  (selectedItem) => selectedItem.value === option.value,
                )
              "
              @change="onMultipleChange(option)"
            />
          </li>
        </template>
        <template v-else>
          <li
            v-for="option in options"
            :key="option.value"
            class="confetti-select-option-item"
            :class="{
              'confetti-select-option-item--selected': isSelected(option),
            }"
            @click.prevent="onChange(option)"
            @keyup.enter.prevent="onChange(option)"
          >
            {{ option.text }}
          </li>
        </template>
      </ul>
      <span class="confetti-select-label">{{ label }}</span>
    </label>
    <ConfettiSpinnerCircle v-if="loading" />
  </div>
</template>

<script>
import { computed, ref } from "@vue/composition-api";
import ConfettiCheckbox from "../forms/ConfettiCheckbox";
import ConfettiIcon from "../ConfettiIcon";
import ConfettiSpinnerCircle from "../../components/layout/ConfettiSpinnerCircle";
import { handleScreenIncursion } from "../../utils";

export default {
  name: "ConfettiSelect",
  components: { ConfettiCheckbox, ConfettiIcon, ConfettiSpinnerCircle },
  props: {
    disabled: {
      type: Boolean,
    },
    label: {
      type: String,
      required: true,
    },
    isMultipleSelect: {
      type: Boolean,
    },
    loading: {
      type: Boolean,
    },
    multipleSelected: {
      type: Array,
      default: () => [],
    },
    maxDisplayedItems: {
      type: Number,
      default: 0,
    },
    options: {
      type: Array,
      required: true,
    },
    selected: {
      type: Object,
      default: null,
    },
  },

  setup(props, { emit }) {
    const showList = ref(false);
    const root = ref(null);

    const iconClass = computed(() => {
      const className = showList.value ? "confetti-select-icon-rotate" : "";

      return className;
    });

    const displayedValue = computed(() => {
      if (props.isMultipleSelect) {
        const needsAppendix =
          props.maxDisplayedItems > 0 &&
          props.maxDisplayedItems < props.multipleSelected.length;
        let displayedItems = props.multipleSelected;
        let appendix = "";

        if (needsAppendix) {
          displayedItems = displayedItems.slice(0, props.maxDisplayedItems);
          appendix = getAppendix();
        }

        return displayedItems.map((item) => item.text).join(", ") + appendix;
      }

      return props.selected?.text || "";
    });

    function isSelected(option) {
      if (option && this.selected) {
        const isSelectedValueEqualToSelectedOption =
          option.value === this.selected.value;

        return isSelectedValueEqualToSelectedOption;
      }

      return false;
    }

    function close() {
      document.removeEventListener("click", onClick);
      showList.value = false;
    }

    function onChange(option) {
      close();
      emit("change", option);
    }

    function onClick({ target }) {
      if (!root.value.contains(target)) {
        close();
      }
    }

    function onMultipleChange(option) {
      const index = props.multipleSelected.findIndex(
        (selected) => selected.value === option.value,
      );

      if (index > -1) {
        // eslint-disable-next-line vue/no-mutating-props
        props.multipleSelected.splice(index, 1);
      } else {
        // eslint-disable-next-line vue/no-mutating-props
        props.multipleSelected.push(option);
      }

      emit("change", props.multipleSelected);
    }

    async function toggleList() {
      if (!showList.value) {
        document.addEventListener("click", onClick);
      }

      showList.value = !showList.value;

      if (showList.value) {
        await this.$nextTick();

        const optionsElement = root.value.querySelector(
          ".confetti-select-option-list",
        );

        handleScreenIncursion(optionsElement);
      }
    }

    function getAppendix() {
      const howManyMore =
        props.multipleSelected.length - props.maxDisplayedItems;

      return ` (+ ${howManyMore} more)`;
    }

    return {
      showList,
      root,
      iconClass,
      displayedValue,
      isSelected,
      onChange,
      onMultipleChange,
      toggleList,
      // eslint-disable-next-line vue/no-unused-properties
      getAppendix,
    };
  },
};
</script>

<style lang="scss" scoped>
@import "confetti/scss/variables/colors";
@import "confetti/scss/variables/z-index";

.confetti-select {
  position: relative;
}

.multiple-select-option-item {
  padding: 0;

  ::v-deep .confetti-checkbox-label {
    cursor: pointer;
    inline-size: 100%;
    padding-block: 1.4rem;
    padding-inline: 1.6rem;
  }
}

.confetti-spinner-circle {
  cursor: not-allowed;
  inset-block: 50% 0;
  inset-inline: 0;
  position: absolute;
  transform: translateY(-50%);
  z-index: $spinner-z-index;
}

.confetti-select-option-list {
  display: none;

  &--open {
    display: block;
    max-block-size: 90vh;
    overflow-y: auto;
  }
}

.confetti-select-option-item--selected {
  background-color: $grey-2;
}
</style>
